import {
  DEFAULT_MARKETPLACE_FEES,
  type CreateListingInput,
  type Listing,
  type ListingAnalytics,
  type ListingStatus,
  type MarketplaceFees,
  type PricingRuleSet,
  type UpdateListingInput,
} from '@carsfinders/contracts';

import { apiFetch } from '../../lib/api';

/**
 * Admin resale-marketplace API (Phase 18 — moderation + own-stock). Calls
 * api-node directly:
 *   GET  /api/admin/marketplace/listings                ?status=&listingMode=&page=
 *   POST /api/admin/marketplace/from-won-car/:wonCarId  { price, currency?, title? }
 *   POST /api/admin/marketplace/:id/approve             moderation → active
 *   POST /api/admin/marketplace/:id/reject              { reason }  (≥10 chars)
 *   POST /api/admin/marketplace/:id/transition          { to, adminOverride?, reason? }
 *   GET  /api/marketplace/listings/:id                  public detail (active/reserved/sold)
 *   GET  /api/pricing/rules/current                     fee rates for the preview
 *
 * plus the seller-cabinet routes the «Мої оголошення» tab drives as the admin
 * itself (MarketplaceSellerRoleGuard admits role `admin`):
 *   GET   /api/marketplace/listings/mine             the admin's own listings
 *   GET   /api/marketplace/listings/sales-count      sold this year (tax ordinal)
 *   POST  /api/marketplace/listings                  create a consignment draft
 *   PATCH /api/marketplace/listings/:id              edit a draft/rejected listing
 *   POST  /api/marketplace/listings/:id/submit       draft → moderation
 *   GET   /api/marketplace/listings/:id/analytics    views/saves/leads
 *
 * The admin feed is what makes the moderation queue real: the PUBLIC browse list
 * serves `active` only, so before it existed a listing sitting in `moderation`
 * was invisible to the API and the queue had to be a browser-local id registry.
 */

export interface AdminListingsParams {
  /** One ListingStatus or a comma-separated set. Absent → every status. */
  status?: string;
  listingMode?: 'own_stock' | 'consignment';
  page?: number;
  limit?: number;
  sortBy?: 'createdAt' | 'publishedAt';
  order?: 'asc' | 'desc';
}

export interface AdminListingsPage {
  success?: boolean;
  items: Listing[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

/**
 * The moderation feed. `status=moderation` IS the queue; an unfiltered call is
 * the full inventory view used by the own-stock tab.
 */
export async function fetchAdminListings(
  params: AdminListingsParams = {},
): Promise<AdminListingsPage> {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') search.set(key, String(value));
  }
  const query = search.toString();
  const body = await apiFetch<AdminListingsPage>(
    `admin/marketplace/listings${query ? `?${query}` : ''}`,
  );
  return {
    ...body,
    items: Array.isArray(body.items) ? body.items : [],
    total: body.total ?? 0,
  };
}

export interface ListingActionResponse {
  success?: boolean;
  listingId?: string;
  from?: ListingStatus;
  status?: ListingStatus;
  error?: string;
}

export interface CreateOwnStockBody {
  price: number;
  currency?: string;
  title?: string;
}

/** Create an own_stock listing from a won car (provenance auto-fills server-side). */
export function createOwnStockListing(
  wonCarId: string,
  body: CreateOwnStockBody,
): Promise<{ success?: boolean; listing: Listing }> {
  return apiFetch(`admin/marketplace/from-won-car/${wonCarId}`, {
    method: 'POST',
    body,
  });
}

/** moderation → active. */
export function approveListing(
  listingId: string,
): Promise<ListingActionResponse> {
  return apiFetch(`admin/marketplace/${listingId}/approve`, { method: 'POST' });
}

/** moderation → rejected. The API rejects a reason shorter than 10 characters. */
export function rejectListing(
  listingId: string,
  reason: string,
): Promise<ListingActionResponse> {
  return apiFetch(`admin/marketplace/${listingId}/reject`, {
    method: 'POST',
    body: { reason },
  });
}

/** Generic status change (reserved / sold / archived / active, or an override). */
export function transitionListing(
  listingId: string,
  to: ListingStatus,
  options: { adminOverride?: boolean; reason?: string } = {},
): Promise<ListingActionResponse> {
  return apiFetch(`admin/marketplace/${listingId}/transition`, {
    method: 'POST',
    body: { to, ...options },
  });
}

/*
 * NOTE: the public-detail read (`GET /api/marketplace/listings/:id`) is gone
 * from this client. Each queue row used to probe it for the car's details, but
 * it serves `active`/`reserved`/`sold` only — so it returned null for exactly
 * the in-moderation listings the queue exists to show. fetchAdminListings above
 * returns the full document for every status instead.
 */

/** Active marketplace fee rates; falls back to the seeded defaults. */
export async function fetchMarketplaceFees(): Promise<MarketplaceFees> {
  try {
    const rules = await apiFetch<PricingRuleSet>('pricing/rules/current');
    return rules.marketplace ?? DEFAULT_MARKETPLACE_FEES;
  } catch {
    return DEFAULT_MARKETPLACE_FEES;
  }
}

// ── Seller cabinet (the admin's own consignment listings) ────────────────────

/** Every listing of the session user, every status, newest first. */
export async function fetchMyListings(): Promise<Listing[]> {
  const body = await apiFetch<{ items?: Listing[] }>('marketplace/listings/mine');
  return Array.isArray(body.items) ? body.items : [];
}

/** Cars the session user sold this calendar year — fixes the next sale's tax ordinal. */
export async function fetchSalesCount(): Promise<number> {
  const body = await apiFetch<{ soldCount?: number }>('marketplace/listings/sales-count');
  return body.soldCount ?? 0;
}

export function createSellerListing(
  input: CreateListingInput,
): Promise<{ success?: boolean; listing: Listing }> {
  return apiFetch('marketplace/listings', { method: 'POST', body: input });
}

export function updateSellerListing(
  listingId: string,
  patch: UpdateListingInput,
): Promise<{ success?: boolean; listing: Listing }> {
  return apiFetch(`marketplace/listings/${encodeURIComponent(listingId)}`, {
    method: 'PATCH',
    body: patch,
  });
}

/** draft|rejected → moderation. */
export function submitSellerListing(listingId: string): Promise<ListingActionResponse> {
  return apiFetch(`marketplace/listings/${encodeURIComponent(listingId)}/submit`, {
    method: 'POST',
  });
}

export async function fetchListingAnalytics(listingId: string): Promise<ListingAnalytics> {
  const body = await apiFetch<{ analytics: ListingAnalytics }>(
    `marketplace/listings/${encodeURIComponent(listingId)}/analytics`,
  );
  return body.analytics;
}
